import type { EnvironmentKey, DomainEntity } from '@web-config-app/core';
import { useEntityGet, useEntityList } from '@web-config-app/core-react';
import { isUsingConfigAppBackend } from '@web-config-app/common';

/**
 * Fetches data for a referenced entity by ID. Used by entity reference controls to display the
 * referenced entity's name/status in the form.
 *
 * Three resolution paths depending on what we're referencing and which backend the entity uses:
 *
 * 1. Relationship reference + config-app-backend → ConfigEntity by config-entity-id (path GET).
 * 2. Domain-id reference + config-app-backend → list `/v1/config-entities` filtered by
 *    `domainEntityID` + `domainEntityType`, take the first match. Required because the value
 *    stored on the attribute is the domain entity id, not the config entity id.
 * 3. Non-CACT-BE entity (`isUsingConfigAppBackend: false`) → DomainEntity GET against the entity's own endpoints.
 */

export interface UseReferencedEntityDataProps {
  domainEntity: DomainEntity;
  instanceId?: string;
  environment: EnvironmentKey;
  isRelationshipReference: boolean;
}

export const useReferencedEntityData = ({
  domainEntity,
  instanceId,
  environment,
  isRelationshipReference,
}: UseReferencedEntityDataProps) => {
  const isConfigAppBackendEnabled = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });
  const useDomainIdLookup =
    isConfigAppBackendEnabled && !isRelationshipReference;

  const getResult = useEntityGet({
    entity: domainEntity,
    instanceId,
    environment,
    options: { enabled: !useDomainIdLookup },
  });

  const listResult = useEntityList({
    entity: domainEntity,
    environment,
    domainEntityType: domainEntity.type,
    domainEntityId: instanceId,
    enabled: useDomainIdLookup && Boolean(instanceId),
  });

  if (useDomainIdLookup) {
    return {
      referencedEntity: listResult.data?.[0],
      isLoading: listResult.isLoading,
      isError: listResult.isError,
    };
  }

  return {
    referencedEntity: getResult.data,
    isLoading: getResult.isLoading,
    isError: getResult.isError,
  };
};
